import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { CustomerAuthProvider } from './lib/customerAuth';
import { CartProvider } from './lib/cart';
import { Toaster } from 'sonner';
import './App.css';

// Admin pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Products from './pages/Products';
import ProductEdit from './pages/ProductEdit';
import Inventory from './pages/Inventory';
import Customers from './pages/Customers';
import WholesaleApprovals from './pages/Wholesale';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Shipping from './pages/Shipping';
import ServiceEnquiries from './pages/ServiceEnquiries';
import AdminUsers from './pages/AdminUsers';
import CRM from './pages/CRM';
import TaxGroups from './pages/TaxGroups';
import Units from './pages/Units';
import Suppliers from './pages/Suppliers';
import BusinessSettings from './pages/BusinessSettings';
import HomeCMS from './pages/HomeCMS';
import SystemAdmin from './pages/SystemAdmin';

// Customer pages
import Home from './pages/customer/Home';
import ProductListing from './pages/customer/ProductListing';
import ProductDetail from './pages/customer/ProductDetail';
import Cart from './pages/customer/Cart';
import Checkout from './pages/customer/Checkout';
import OrderSuccess from './pages/customer/OrderSuccess';
import { AccountProfile, AccountOrders, AccountOrderDetail } from './pages/customer/Account';
import Services from './pages/customer/Services';
import CustomerWholesale from './pages/customer/Wholesale';
import About from './pages/customer/About';
import Contact from './pages/customer/Contact';
import Policy from './pages/customer/Policy';
import WhatsAppFAB from './components/WhatsAppFAB';
import TopBar from './components/TopBar';
import ScrollToTop from './components/ScrollToTop';

const ADMIN = '/dharmikdashboard';

function RequireAuth({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    if (!user) return <Navigate to={`${ADMIN}/login`} state={{ from: location }} replace />;
    return children;
}
function RoleGate({ roles, children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to={`${ADMIN}/login`} replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to={ADMIN} replace />;
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            {/* CUSTOMER STOREFRONT (public) */}
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductListing />} />
            <Route path="/search" element={<ProductListing />} />
            <Route path="/category/:slug" element={<ProductListing />} />
            <Route path="/product/:slugOrId" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-success/:orderNumber" element={<OrderSuccess />} />
            <Route path="/account" element={<AccountProfile />} />
            <Route path="/account/orders" element={<AccountOrders />} />
            <Route path="/account/orders/:id" element={<AccountOrderDetail />} />
            <Route path="/services" element={<Services />} />
            <Route path="/wholesale" element={<CustomerWholesale />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/policies/:kind" element={<Policy />} />

            {/* ADMIN PANEL under /dharmikdashboard */}
            <Route path={`${ADMIN}/login`} element={<Login />} />
            <Route path={`${ADMIN}`} element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path={`${ADMIN}/orders`} element={<RequireAuth><Orders /></RequireAuth>} />
            <Route path={`${ADMIN}/orders/:id`} element={<RequireAuth><OrderDetail /></RequireAuth>} />
            <Route path={`${ADMIN}/products`} element={<RequireAuth><Products /></RequireAuth>} />
            <Route path={`${ADMIN}/products/:id`} element={<RequireAuth><ProductEdit /></RequireAuth>} />
            <Route path={`${ADMIN}/categories`} element={<RequireAuth><RoleGate roles={['super_admin', 'inventory_manager']}><Categories /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/brands`} element={<RequireAuth><RoleGate roles={['super_admin', 'inventory_manager']}><Brands /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/inventory`} element={<RequireAuth><Inventory /></RequireAuth>} />
            <Route path={`${ADMIN}/customers`} element={<RequireAuth><Customers /></RequireAuth>} />
            <Route path={`${ADMIN}/wholesale`} element={<RequireAuth><RoleGate roles={['super_admin', 'sales']}><WholesaleApprovals /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/service-enquiries`} element={<RequireAuth><RoleGate roles={['super_admin', 'sales']}><ServiceEnquiries /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/crm`} element={<RequireAuth><RoleGate roles={['super_admin', 'sales']}><CRM /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/tax-groups`} element={<RequireAuth><RoleGate roles={['super_admin']}><TaxGroups /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/units`} element={<RequireAuth><RoleGate roles={['super_admin', 'inventory_manager']}><Units /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/suppliers`} element={<RequireAuth><RoleGate roles={['super_admin', 'inventory_manager']}><Suppliers /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/business-settings`} element={<RequireAuth><RoleGate roles={['super_admin']}><BusinessSettings /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/home-cms`} element={<RequireAuth><RoleGate roles={['super_admin']}><HomeCMS /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/system`} element={<RequireAuth><RoleGate roles={['super_admin']}><SystemAdmin /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/shipping`} element={<RequireAuth><RoleGate roles={['super_admin']}><Shipping /></RoleGate></RequireAuth>} />
            <Route path={`${ADMIN}/users`} element={<RequireAuth><RoleGate roles={['super_admin']}><AdminUsers /></RoleGate></RequireAuth>} />

            {/* Legacy redirects */}
            <Route path="/login" element={<Navigate to={`${ADMIN}/login`} replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <CustomerAuthProvider>
            <CartProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <TopBar />
                        <AppRoutes />
                        <WhatsAppFAB />
                        <Toaster position="top-right" richColors />
                    </BrowserRouter>
                </AuthProvider>
            </CartProvider>
        </CustomerAuthProvider>
    );
}
